import { makeAutoObservable, runInAction } from "mobx";
import eventsAPIHandler from "../handlers/EventsAPIHandler";

class OverpressureEventStore {
  overpressureEventsMap = new Map(); // month → { all: { v1: [], v2: [] }, device_id: { v1: [], v2: [] } }
  lastIdMap = new Map(); // Map of deviceId -> last _id
  globalLastId = null; // For 'all devices' fetch
  refreshInterval = null; // In minutes
  refreshTimer = null;
  monthlySummaryMap = new Map();

  constructor(refreshInterval = null) {
    makeAutoObservable(this);
    this.refreshInterval = refreshInterval;

    if (this.refreshInterval !== null) {
      this.startAutoRefresh();
    }
  }

  startAutoRefresh() {
    if (this.refreshTimer) clearInterval(this.refreshTimer);

    this.refreshTimer = setInterval(
      () => {
        this.loadOverpressureEventsAll();
      },
      this.refreshInterval * 60 * 1000
    );
  }

  stopAutoRefresh() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  setRefreshInterval(minutes) {
    this.refreshInterval = minutes;
    if (minutes === null) {
      this.stopAutoRefresh();
    } else {
      this.startAutoRefresh();
    }
  }

  // updateMonthlySummary(event) {
  //   const deviceId = event.device_id;
  //   const valve = event.metadata?.valve || "unknown";
  //   let rawDate = event.timestamp;
  //   if (typeof rawDate === "object" && rawDate.$date) {
  //     rawDate = rawDate.$date;
  //   }
  //   const date = new Date(rawDate);
  //   const month = date.toISOString().slice(0, 7);

  //   if (!this.monthlySummaryMap.has(month)) {
  //     this.monthlySummaryMap.set(month, {});
  //   }

  //   const monthEntry = this.monthlySummaryMap.get(month);

  //   if (!monthEntry.all) monthEntry.all = { v1: 0, v2: 0 };
  //   if (!monthEntry.all[valve]) monthEntry.all[valve] = 0;
  //   monthEntry.all[valve] += 1;

  //   if (!monthEntry[deviceId]) monthEntry[deviceId] = { v1: 0, v2: 0 };
  //   if (!monthEntry[deviceId][valve]) monthEntry[deviceId][valve] = 0;
  //   monthEntry[deviceId][valve] += 1;
  // }

  updateMonthlySummary(event) {
    const deviceId = event.device_id;

    let rawDate = event.timestamp;
    // console.log(rawDate);
    if (typeof rawDate === "object" && rawDate.$date) {
      rawDate = rawDate.$date;
    }

    const date = new Date(rawDate);
    if (isNaN(date.getTime())) {
      console.warn("Skipping invalid date in updateMonthlySummary:", rawDate);
      return;
    }

    const month = date.toISOString().slice(0, 7);

    const rawValve = event.metadata?.valve;
    const valve = rawValve === "v1" || rawValve === "v2" ? rawValve : null;
    if (!valve) return;

    if (!this.monthlySummaryMap.has(month)) {
      this.monthlySummaryMap.set(month, {});
    }

    const monthEntry = this.monthlySummaryMap.get(month);

    if (!monthEntry.all) monthEntry.all = { v1: 0, v2: 0 };
    monthEntry.all[valve] += 1;

    if (!monthEntry[deviceId]) monthEntry[deviceId] = { v1: 0, v2: 0 };
    monthEntry[deviceId][valve] += 1;
  }

  insertEventByMonthValve(event) {
    const deviceId = event.device_id;

    let rawDate = event.timestamp;
    if (typeof rawDate === "object" && rawDate.$date) {
      rawDate = rawDate.$date;
    }
    const date = new Date(rawDate);
    if (isNaN(date.getTime())) return; // ← ✅ prevent invalid date crash

    const month = date.toISOString().slice(0, 7);

    const rawValve = event.metadata?.valve;
    const valve = rawValve === "v1" || rawValve === "v2" ? rawValve : null;
    if (!valve) return;

    if (!this.overpressureEventsMap.has(month)) {
      this.overpressureEventsMap.set(month, {});
    }

    const monthEntry = this.overpressureEventsMap.get(month);

    if (!monthEntry.all) monthEntry.all = { v1: [], v2: [] };
    if (!monthEntry.all[valve]) monthEntry.all[valve] = [];
    if (!monthEntry[deviceId]) monthEntry[deviceId] = { v1: [], v2: [] };
    if (!monthEntry[deviceId][valve]) monthEntry[deviceId][valve] = [];

    const allList = monthEntry.all[valve];
    const deviceList = monthEntry[deviceId][valve];
    const alreadyExists = deviceList.some((e) => e._id === event._id);
    if (!alreadyExists) {
      allList.unshift(event);
      deviceList.unshift(event);
    }
  }

  async loadOverpressureEventsAll() {
    try {
      const data = await eventsAPIHandler.fetchOverpressureEventsAll(
        this.globalLastId
      );
      console.log("Overpressure from loadOverpresssureEventsAll: ", data);
      runInAction(() => {
        const seenDevices = new Set();
        for (const event of data) {
          const deviceId = event.device_id;
          // this.overpressureEventsMap.get(deviceId).push(event);
          this.insertEventByMonthValve(event);
          this.updateMonthlySummary(event);
          // this.lastIdMap.set(deviceId, event._id);
          if (!seenDevices.has(deviceId)) {
            this.lastIdMap.set(deviceId, event?._id?.$oid);
            seenDevices.add(deviceId);
          }
        }
        if (data.length > 0) {
          // this.globalLastId = data[data.length - 1]._id;
          this.globalLastId = data[0]?._id?.$oid;
        }
      });
    } catch (e) {
      console.error("❌ Failed to load all overpressure events:", e);
    }
  }

  async loadOverpressureEventsForDevice(deviceId) {
    try {
      const lastId = this.lastIdMap.get(deviceId) || null;
      const data = await eventsAPIHandler.fetchOverpressureEventsForDevice(
        deviceId,
        lastId
      );
      runInAction(() => {
        // this.overpressureEventsMap.get(deviceId).push(...data);
        this.insertEventByMonthValve(data);
        this.updateMonthlySummary(data);
        if (data.length > 0) {
          // this.lastIdMap.set(deviceId, data[data.length - 1]._id);
          this.lastIdMap.set(deviceId, data[0]?._id?.$oid);
        }
      });
    } catch (e) {
      console.error(
        `❌ Failed to load overpressure events for ${deviceId}:`,
        e
      );
    }
  }

  async loadOverpressureEventsForDeviceSet(deviceIds) {
    try {
      const data = await eventsAPIHandler.fetchOverpressureForDeviceSet(
        deviceIds,
        this.globalLastId
      );
      runInAction(() => {
        const seenDevices = new Set();
        for (const event of data) {
          const deviceId = event.device_id;
          // this.overpressureEventsMap.get(deviceId).push(event);
          this.insertEventByMonthValve(event);
          this.updateMonthlySummary(event);
          // this.lastIdMap.set(deviceId, event._id);
          if (!seenDevices.has(deviceId)) {
            this.lastIdMap.set(deviceId, event?._id?.$oid);
            seenDevices.add(deviceId);
          }
        }
        if (data.length > 0) {
          // this.globalLastId = data[data.length - 1]._id;
          this.globalLastId = data[0]?._id?.$oid;
        }
      });
    } catch (e) {
      console.error("❌ Failed to load overpressure events for device set:", e);
    }
  }

  // getEventsByDevice(deviceId) {
  //   return this.overpressureEventsMap.get(deviceId) || [];
  // }

  // getAllEvents() {
  //   const all = [];
  //   for (const events of this.overpressureEventsMap.values()) {
  //     all.push(...events);
  //   }
  //   return all;
  // }

  getEventsForMonth(month, deviceId = "all") {
    const monthEntry = this.overpressureEventsMap.get(month);
    if (!monthEntry) return { v1: [], v2: [] };
    return monthEntry[deviceId] || { v1: [], v2: [] };
  }

  getMonthlySummary(deviceId = null) {
    const result = {};

    const now = new Date();
    for (let i = 0; i < 3; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = d.toISOString().slice(0, 7); // "YYYY-MM"

      const entry = this.monthlySummaryMap.get(key);
      const counts = entry?.[deviceId || "all"] || { v1: 0, v2: 0 };

      result[key] = {
        v1: counts.v1 || 0,
        v2: counts.v2 || 0,
      };
    }

    // console.log("Monthly Summary:", toJS(this.monthlySummaryMap));

    return result;
  }

  injectMockData(mockEvents = []) {
    runInAction(() => {
      for (const event of mockEvents) {
        this.insertEventByMonthValve(event);
        this.updateMonthlySummary(event);

        const deviceId = event.device_id;
        this.lastIdMap.set(deviceId, event._id?.$oid || `mock_${Date.now()}`);
        this.globalLastId = event._id?.$oid || `mock_${Date.now()}`;
      }
    });
  }
}

const overpressureEventStore = new OverpressureEventStore();

// overpressureEventStore.injectMockData([
//   {
//     _id: { $oid: "mock_1" },
//     device_id: "373137381932343431332F57",
//     field: "v1_avg_line_pressure",
//     value: 5300,
//     threshold: 5000,
//     timestamp: new Date().toISOString(),
//     source_log_id: 101,
//     metadata: {
//       valve: "v1",
//       pressure_unit: "psi",
//       set_point: 5000,
//     },
//   },
//   {
//     _id: { $oid: "mock_2" },
//     device_id: "373137381932343431332F57",
//     field: "v2_avg_line_pressure",
//     value: 5200,
//     threshold: 5000,
//     timestamp: new Date().toISOString(),
//     source_log_id: 102,
//     metadata: {
//       valve: "v2",
//       pressure_unit: "psi",
//       set_point: 5000,
//     },
//   },
// ]);
export default overpressureEventStore;
